import React from 'react';
import { ScrollIntoView } from './ScrollIntoView';
import { Box, ChatMessageSeparator } from 'pcln-design-system';
import { TravelBotChatSingleDialogGeneric } from './TravelBotChatSingleDialogGeneric';
import HandleSetiActions from './components/HandleSetiActions';
import { QuickActions } from './QuickActions';
import { QuickActionsChips } from './QuickActionsChips';
import { BlockingInputComponent } from './BlockingInputComponent';
import TripProtectionMarker from './components/TripProtectionMarker/TripProtectionMarker';
import Trips from './components/Trips/Trips';
import SignOutActions from './components/SignOutActions/SignOutActions';
import { HotelDetailCardsMessage } from './HotelComponent/HotelDetailCard';
import CalendarComponent from './components/CalendarComponent/CalendarComponent';
import { ButtonChipsAction } from './components/ButtonChipsAction/ButtonChipsAction';
import DisplayFeedbackMarkers from './components/DisplayFeedbackMarker/DisplayFeedbackMarker';
import ChatMessageErrorBoundary from './components/ErrorBoundary/ChatMessageErrorBoundary';
import { TextWithInlineMetadataComponent } from './components/MetadataMarker/InlineMetadataMarkerComponent';
import { OpenMapButton } from './components/Maps/OpenMapButton';
import ShowCancelConsequencesMarker from './components/ShowCancelConsequencesMarker/ShowCancelConsequencesMarker';
import styled from 'styled-components';
const DisappearingGradientBox = /*#__PURE__*/styled(Box).withConfig({
  displayName: "ChatMessagesCollections__DisappearingGradientBox",
  componentId: "sc-1y9y9kj-0"
})(["position:relative;&::before{content:'';position:absolute;top:0;left:0;width:16px;height:", ";background:linear-gradient(to left,rgba(255,255,255,0),rgba(255,255,255,1));pointer-events:none;z-index:1000;opacity:", ";}&::after{content:'';position:absolute;top:0;right:0;width:16px;height:", ";background:linear-gradient(to right,rgba(255,255,255,0),rgba(255,255,255,1));pointer-events:none;z-index:1000;opacity:", ";}"], ({
  isLastMessage
}) => isLastMessage ? '79px' : '0px', ({
  showStartGradient
}) => showStartGradient ? 1 : 0, ({
  isLastMessage
}) => isLastMessage ? '79px' : '0px', ({
  showEndGradient
}) => showEndGradient ? 1 : 0);
export const ChatMessagesList = ({
  chatMessages,
  isRetrievingAudio,
  markerComponents,
  isLoading,
  debugMode,
  setIsDisablingInputValue,
  setChatMessages,
  submitMessages,
  submitChatMessageInMarkerComponent,
  submitQuickActionMessage,
  isShowMoreListingCards,
  isStandalone,
  messagesEndRef
}) => {
  const chatmessageCount = chatMessages.length - 1;
  const [showQuickActionStartGradient, setShowQuickActionStartGradient] = React.useState(false);
  const [showQuickActionEndGradient, setShowQuickActionEndGradient] = React.useState(true);
  const handleQuickActionScroll = function (e) {
    const element = e.currentTarget;
    const isScrolledToBeginning = element.scrollLeft === 0;
    const isScrolledToEnd = Math.abs(element.scrollWidth - element.clientWidth - element.scrollLeft) < 1;
    setShowQuickActionStartGradient(!isScrolledToBeginning);
    setShowQuickActionEndGradient(!isScrolledToEnd);
  };
  return React.createElement(React.Fragment, null, chatMessages.map((m, i) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const isLastMessage = chatMessages.length - 1 === i;
    const hasOtherMessageHandler = (m, chatMessage) => {
      const mappedChatMessage = chatMessage.map(result => JSON.stringify(result));
      const index = mappedChatMessage.indexOf(JSON.stringify(m));
      return chatMessage.length - 1 > index;
    };
    // hide the last audio message if we're retreiving audio (since another mechanism shows the text for the audio while it's playing)
    // Since marker components can come back as two different messages, we also have to check the second to last message
    if (isRetrievingAudio) {
      const lastIndex = chatMessages.length - 1;
      const secondLastIndex = chatMessages.length - 2;
      // Case 1: Suppress the last message if its role is 'assistant'
      if (((_a = chatMessages[lastIndex]) === null || _a === void 0 ? void 0 : _a.role) === 'assistant' && i === lastIndex) {
        return null;
      }
      // Case 2: Suppress the last message and the message before it if the last message's role is ''
      // and the message before it has a role of 'assistant'. This handles instances where the marker component
      // information comes back in multiple parts (such as for hotel search)
      if (((_b = chatMessages[lastIndex]) === null || _b === void 0 ? void 0 : _b.role) === '' && ((_c = chatMessages[secondLastIndex]) === null || _c === void 0 ? void 0 : _c.role) === 'assistant' && (i === lastIndex || i === secondLastIndex)) {
        return null;
      }
    }
    // Display a predefined Marker Component
    if (markerComponents[m === null || m === void 0 ? void 0 : m.displayComponentName]) {
      const Marker = markerComponents[m.displayComponentName];
      const markerComponentProps = (m === null || m === void 0 ? void 0 : m.markerComponentProps) || {};
      const markerProps = Object.assign(Object.assign({}, markerComponentProps), {
        message: m,
        isLoading,
        chatMessages,
        setIsDisablingInputValue,
        setChatMessages,
        submitMessages
      });
      return React.createElement(React.Fragment, {
        key: `chat-msg-host-app-marker-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: m.displayComponentName
      }, React.createElement(Marker, Object.assign({}, markerProps))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'SEPARATOR_COMPONENT') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-content-separator-${i}`
      }, React.createElement(Box, {
        mb: 4
      }, React.createElement(ChatMessageSeparator, null)));
    }
    if (m.role === 'chatContentSeparator') {
      const content = m.content;
      return React.createElement(ScrollIntoView, {
        key: `chat-msg-content-separator-${m.key}-${i}`
      }, React.createElement(Box, {
        mb: 4
      }, React.createElement(ChatMessageSeparator, {
        message: content
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.role) && (m === null || m === void 0 ? void 0 : m.content)) {
      return React.createElement(ScrollIntoView, {
        key: `chat-msg-generic-message-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'TravelBotChatSingleDialogGeneric'
      }, React.createElement(TravelBotChatSingleDialogGeneric, {
        message: m
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'INLINE_METADATA_MARKER') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-metadata-marker${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'TextWithInlineMetadataComponent'
      }, React.createElement(TextWithInlineMetadataComponent, Object.assign({}, m === null || m === void 0 ? void 0 : m.markerComponentProps))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'FIRE_SETI') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-seit-action-message-${i}`
      }, React.createElement(ChatMessageErrorBoundary, null, React.createElement(HandleSetiActions, {
        setiValues: (_d = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _d === void 0 ? void 0 : _d.setiValues
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'QUICK_ACTIONS') {
      return React.createElement(DisappearingGradientBox, {
        key: `chat-msg-quick-actions-${i}`,
        showStartGradient: showQuickActionStartGradient,
        showEndGradient: showQuickActionEndGradient,
        isLastMessage: isLastMessage
      }, React.createElement(ScrollIntoView, null, React.createElement(ChatMessageErrorBoundary, null, React.createElement(QuickActions, {
        isStandalone: isStandalone,
        isSingleLine: true,
        quickActions: (_e = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _e === void 0 ? void 0 : _e.quickActions,
        hasOtherMessage: hasOtherMessageHandler(m, chatMessages),
        submitQuickActionMessage: submitQuickActionMessage,
        isSeparatorMsg: ((_f = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _f === void 0 ? void 0 : _f.isSeparatorMsg) && chatmessageCount === i,
        onScroll: handleQuickActionScroll
      }))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'QUICK_ACTIONS_CHIPS') {
      return React.createElement(ScrollIntoView, {
        key: `chat-msg-quick-chips-${i}`
      }, React.createElement(ChatMessageErrorBoundary, null, React.createElement(QuickActionsChips, {
        quickActions: (_g = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _g === void 0 ? void 0 : _g.quickActions,
        hasOtherMessage: hasOtherMessageHandler(m, chatMessages),
        submitQuickActionMessage: submitQuickActionMessage,
        isSeparatorMsg: ((_h = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _h === void 0 ? void 0 : _h.isSeparatorMsg) && chatmessageCount === i
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'BUTTON_CHIPS_ACTION') {
      return React.createElement(ScrollIntoView, {
        key: `chat-msg-button-chips-actions-${i}`
      }, React.createElement(ChatMessageErrorBoundary, null, React.createElement(ButtonChipsAction, {
        submitButtonChipActionMessage: submitQuickActionMessage,
        hasOtherMessage: hasOtherMessageHandler(m, chatMessages),
        buttonChipsActions: (_j = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _j === void 0 ? void 0 : _j.quickActions,
        isSeparatorMsg: ((_k = m === null || m === void 0 ? void 0 : m.markerComponentProps) === null || _k === void 0 ? void 0 : _k.isSeparatorMsg) && chatmessageCount === i
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'SESSION_INITIALIZED') {
      return null;
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'HOTEL_CARDS_MARKER') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-hotel-cards-marker-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'HotelDetailCardsMessage'
      }, React.createElement(HotelDetailCardsMessage, Object.assign({}, m === null || m === void 0 ? void 0 : m.markerComponentProps, {
        date: m.date,
        setChatMessages: setChatMessages,
        isShowMoreListingCards: isShowMoreListingCards,
        // Submit message when user apply filter options
        submitMessages: submitChatMessageInMarkerComponent
      }))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'DATE_COMPONENT') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-block-input-marker-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'CalendarComponent'
      }, React.createElement(CalendarComponent, {
        markerComponentProps: m === null || m === void 0 ? void 0 : m.markerComponentProps,
        isLastMessage: isLastMessage
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'BLOCK_INPUT_MARKER') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-block-input-marker-${i}`
      }, React.createElement(BlockingInputComponent, {
        setIsDisablingInputValue: setIsDisablingInputValue
      }));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'PROMPT_TRIP_PROTECTION') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-trip-protection-marker-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'TripProtectionMarker'
      }, React.createElement(TripProtectionMarker, null)));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'DISPLAY_MY_TRIPS') {
      const {
        tripsList: trips,
        hasMoreTrips,
        deepLinkPrompt,
        tripHeaderMsg
      } = (m === null || m === void 0 ? void 0 : m.markerComponentProps) || {};
      return React.createElement(React.Fragment, {
        key: `chat-msg-display-my-trips-marker-${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'Trips'
      }, React.createElement(Trips, {
        trips: trips,
        deepLinkPrompt: deepLinkPrompt,
        tripHeaderMsg: tripHeaderMsg,
        hasMoreTrips: hasMoreTrips,
        submitChatMessageInMarkerComponent: submitChatMessageInMarkerComponent
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'SHOW_SIGNED_OUT_ACTION_BUTTONS') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-signed-out-actions-marker${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'SignOutActions'
      }, React.createElement(SignOutActions, Object.assign({
        submitMessagesFn: submitMessages
      }, (m === null || m === void 0 ? void 0 : m.markerComponentProps) || {}))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'DISPLAY_FEEDBACK_MARKERS') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-display-feedback-marker${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'DisplayFeedbackMarkers'
      }, React.createElement(DisplayFeedbackMarkers, Object.assign({}, m === null || m === void 0 ? void 0 : m.markerComponentProps))));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) === 'SHOW_CANCEL_CONSEQUENCES_MARKER') {
      return React.createElement(React.Fragment, {
        key: `chat-msg-show_cancel_consequences_marker${i}`
      }, React.createElement(ChatMessageErrorBoundary, {
        compnentName: 'ShowCancelConsequencesMarker'
      }, React.createElement(ShowCancelConsequencesMarker, {
        cancelConsequences: Object.assign({}, (m === null || m === void 0 ? void 0 : m.markerComponentProps) || {})
      })));
    }
    if ((m === null || m === void 0 ? void 0 : m.displayComponentName) && debugMode) {
      console.log('Debug Mode', m === null || m === void 0 ? void 0 : m.displayComponentName);
      console.log(JSON.stringify(m, null, 2));
      return React.createElement(ScrollIntoView, {
        key: `chat-msg-marker-${i}`
      }, React.createElement(Box, null, React.createElement(Box, null, "Marker Component Name: ", m === null || m === void 0 ? void 0 : m.displayComponentName), React.createElement(Box, null, "Check dev console for more details of ", m === null || m === void 0 ? void 0 : m.displayComponentName, " component props")));
    }
  }), React.createElement(OpenMapButton, {
    isLoading: isLoading,
    isStandalone: isStandalone
  }), isStandalone ? React.createElement("div", {
    ref: messagesEndRef,
    style: {
      height: 0
    }
  }) : React.createElement(React.Fragment, null));
};